/* eslint-disable comma-dangle */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { CheckOutlined, CopyOutlined, DownOutlined, StopOutlined } from '@ant-design/icons';
import { Input, Tooltip, Card, Checkbox, Dropdown, Button, Form, Space, message } from 'antd';
import { getApps } from '@store/admin/apps/selectors';
import { update } from '@store/admin/apps/actions';
import PageHeader from '@components/common/PageHeader';
import { Retry } from '@src/components/common';
import { useAppDetails } from '../hooks';
import { singleAppPrefix } from '@src/routers/utils';
import { get } from 'lodash';
import { checkCanEditApiManagement, checkIsInternalUser } from '@src/globalStore/profileStore';

const ApplicationEdit = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const isInternalUser = checkIsInternalUser();
  const canEdit = checkCanEditApiManagement();

  const apps = useSelector(getApps);
  const app = get(apps, [params.clientId], {
    title: '',
    clientId: '',
    clientSecret: '',
    inactive: false,
    isInternal: false,
  });

  const onUpdated = (err) => {
    if (err) {
      message.error(err.message);
    }
  };
  const deactivate = (x) => dispatch(update({ ...x, inactive: true }, onUpdated));
  const activate = (x) => dispatch(update({ ...x, inactive: false }, onUpdated));
  const [editingGeneralSettings, setEditingGeneralSettings] = useState(false);
  const [editingClientCredential, setEditingClientCredential] = useState(false);

  const { title, description, clientId, isInternal } = app;

  const {
    data: appData,
    isLoading: isLoading,
    isError: isError,
    refetch: refetchApp,
  } = useAppDetails(clientId);

  useEffect(() => {
    // Whenever clientId changes, force a re-fetch
    if (clientId) {
      refetchApp();
    }
  }, [clientId, refetchApp]);

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 4 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    },
  };
  const tailFormItemLayout = {
    wrapperCol: {
      xs: {
        span: 24,
        offset: 0,
      },
      sm: {
        span: 16,
        offset: 4,
      },
    },
  };

  const handleGeneralSettingSubmit = (values) => {
    dispatch(
      update({ ...app, ...values }, (err) => {
        if (err) {
          message.error(err.message);
          return;
        }
        setEditingGeneralSettings(false);
      }),
    );
  };

  const handleCredentialSubmit = (values) => {
    dispatch(
      update({ ...app, ...values, clientSecret: '' }, (err) => {
        if (err) {
          message.error(err.message);
          return;
        }
        setEditingClientCredential(false);
        refetchApp();
      }),
    );
  };

  const onActivate = () => {
    deactivate(app);
  };

  const onDeactivate = () => {
    activate(app);
  };

  const onEditSettings = () => {
    setEditingGeneralSettings((status) => !status);
  };

  const onEditCredentials = () => {
    setEditingClientCredential((status) => !status);
  };

  return (
    <Retry isLoading={isLoading} showError={isError} fetchData={refetchApp}>
      <PageHeader
        style={{
          marginTop: '5px',
          marginLeft: '1px',
          backgroundColor: 'white',
        }}
        title={app.title}
        back={<Link to={`${singleAppPrefix}/manage/apps/list`}>Applications</Link>}
        extra={
          canEdit
            ? [
                <Dropdown
                  key="1"
                  menu={{
                    items: [
                      !app.inactive
                        ? {
                            key: 'deactivate',
                            label: (
                              <Space>
                                <StopOutlined />
                                <span>Deactivate</span>
                              </Space>
                            ),
                            onClick: onActivate,
                          }
                        : {
                            key: 'activate',
                            label: (
                              <Space>
                                <CheckOutlined />
                                <span>Activate</span>
                              </Space>
                            ),
                            onClick: onDeactivate,
                          },
                    ].filter(Boolean),
                  }}
                >
                  <Button>
                    {app.inactive ? <span>Inactive</span> : <span>Active</span>}
                    <DownOutlined />
                  </Button>
                </Dropdown>,
              ]
            : []
        }
      />
      <Card
        title="General Settings"
        extra={
          canEdit && (
            <Button onClick={onEditSettings}>{editingGeneralSettings ? 'Cancel' : 'Edit'}</Button>
          )
        }
      >
        <Form
          {...formItemLayout}
          onFinish={handleGeneralSettingSubmit}
          initialValues={{ title, description, isInternal: isInternal || false }}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[
              {
                required: true,
                message: 'Please input your application title',
              },
            ]}
          >
            <Input disabled={!editingGeneralSettings} />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input.TextArea disabled={!editingGeneralSettings} />
          </Form.Item>
          {isInternalUser && (
            <Form.Item label="For Internal Use" name="isInternal" valuePropName="checked">
              <Checkbox disabled={!editingGeneralSettings} />
            </Form.Item>
          )}
          {editingGeneralSettings && (
            <Form.Item {...tailFormItemLayout}>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card
        title="Client Credential"
        extra={
          canEdit && (
            <Button onClick={onEditCredentials}>
              {editingClientCredential ? 'Cancel' : 'Edit'}
            </Button>
          )
        }
      >
        <Form
          {...formItemLayout}
          onFinish={handleCredentialSubmit}
          initialValues={{ clientId, clientSecret: appData ? appData.clientSecret : '' }}
        >
          <Form.Item
            label="Client ID"
            name="clientId"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              disabled
              addonAfter={
                <Tooltip title="Copy to clipboard">
                  <CopyToClipboard text={app.clientId}>
                    <CopyOutlined style={{ lineHeight: 1 }} />
                  </CopyToClipboard>
                </Tooltip>
              }
            />
          </Form.Item>
          <Form.Item
            label="Client Secret"
            name="clientSecret"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input
              type="password"
              disabled
              addonAfter={
                <Tooltip title="Copy to clipboard">
                  <CopyToClipboard text={appData?.clientSecret}>
                    <CopyOutlined style={{ lineHeight: 1 }} />
                  </CopyToClipboard>
                </Tooltip>
              }
            />
          </Form.Item>
          {editingClientCredential && (
            <Form.Item {...tailFormItemLayout}>
              <Button type="primary" htmlType="submit">
                Generate New Client Secret
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </Retry>
  );
};

export default ApplicationEdit;
